import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { api, errText, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/Primitives";
import { vocab } from "@/lib/vocab";


const FALLBACKS = [
  "https://images.unsplash.com/photo-1572853566597-b83cde546912?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1604782666037-3c63d50052db?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1511306162219-1c5a469ab86c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1515660132469-bb1bc0444005?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1759960034333-298883b34d3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

export function PreviewGrid({ eyebrow, title, body, modules, unlockedRoute, unlockedLabel }) {
  const { data } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/home")).data });
  const imgs = [...(data?.launch?.posts || []).map((a) => fileUrl(a.thumbnail_url)), ...(data?.brand?.mood_board || []).map((m) => fileUrl(m.url))];
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-8">
      <SectionTitle eyebrow={eyebrow} title={title} body={body} action={<Link to={unlockedRoute} className="btn-outline w-fit" data-testid="preview-primary-cta">{unlockedLabel}</Link>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="preview-grid">
        {modules.map((m, i) => (
          <div key={m.title} className="card-lux overflow-hidden relative" data-testid={`preview-module-${i}`}>
            <div className="aspect-[4/3] relative bg-muted"><img src={imgs[i] || FALLBACKS[i % FALLBACKS.length]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" /><span className="absolute left-3 top-3 inline-flex items-center gap-1 eyebrow-label !text-[9px] bg-background/80 glass px-2 py-1 rounded"><Lock className="h-3 w-3" />{m.phase}</span></div>
            <div className="p-5"><h3 className="font-display text-xl">{m.title}</h3><p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.body}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Create() {
  const { org, refresh } = useAuth();
  const v = vocab(org);
  const modules = [
    { title: "Social Content Studio", body: "Platform, goal and theme → a batch of designed posts in every template family and format, with carousels.", phase: "Phase 2" },
    { title: v.adCampaign + "s", body: "Three variants per concept in every Meta size, with scripts, hooks and an email announcement.", phase: "Phase 2" },
    { title: "Website chat editing", body: "“Make the hero darker.” Targeted edits to your draft, version history and one-click revert.", phase: "Phase 2" },
    { title: "Digital Products", body: org?.org_type === "business" ? "Ebooks, workbooks and lead magnets as designed PDFs with cover mockups." : "Devotionals, study guides and sermon series companions as designed PDFs.", phase: "Phase 3" },
    { title: "UGC Studio", body: "Authentic scripts rendered as storyboards — scene image, spoken line, shot direction.", phase: "Phase 3" },
    { title: v.launch, body: "A complete launch plan: offer, messaging, timeline, email sequence and announcement creatives.", phase: "Phase 3" },
  ];
  const switchToSite = async () => {
    try { await api.post("/org/website-mode", { mode: "legacystack" }); await refresh(); toast.success("Website generation is back on — open Launch Kit to build it"); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <>
      <PreviewGrid eyebrow="Create" title="Every generator, one brand." body="Your Launch Kit is the first of many. These studios unlock next — each reading from the same Brand Tokens." modules={modules} unlockedRoute="/launch" unlockedLabel="Open Launch Kit" />
      <div className="max-w-7xl mx-auto px-5 py-5"><Link className="btn-outline" to="/compose">Compose a post from your photo and copy</Link></div>
      <div className="max-w-7xl mx-auto px-5 py-5"><Link className="btn-gold" to="/generate-post">Generate a new photo, caption and branded post</Link></div>
      {org?.website_mode === "external" && (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-12 text-xs text-muted-foreground" data-testid="build-site-option">
          You're keeping your own website ({org.website_url}). <button type="button" onClick={switchToSite} className="text-gold hover:underline" data-testid="build-legacystack-site">Build a LegacyStack website</button> whenever you like — no rush.
        </div>
      )}
    </>
  );
}
